// Ship by job: the load box (Shipping hub, Phases 2-4, 2026-10-28; behind GET /features shipping_signing.job_loads).
// Finished jobs that are not on a sales order (Bad Idea) have no pallet in JobQ, so every load NAMES its pallet by its
// label: a scanned beacon or pallet label, a typed number, or "New pallet without a beacon". With a truck / where it
// goes / the driver, the ticked lines ship with a load record:
//   POST /shipping/finalize_batch {..., load: {client_request_id, pallets: [{seq: 1, label, method, code}],
//        task_pallets: {<task_id>: 1}, vehicle_id | vehicle_text, destination, driver_user_id | driver_name}}
// Billing is unchanged (the ship date still drives the weekly Bad Idea invoice). Anything not loaded here still ships
// on the nightly Bad Idea auto-ship, just without a load record. No signing QR on job loads.
import React, { useRef, useState } from 'react';
import { createEntryTimer } from '../pallets/palletCodes';
import { newRequestId } from '../signing/signRequestsApi';
import {
  DriverPicker, EMPTY_PICK, TruckPicker, driverFields, truckFields, useDrivers, useVehicles,
} from './loadPickers';

export const DEFAULT_DESTINATION = 'Bad Idea warehouse';
const LABEL_MAX = 60;

/** A fresh load box. */
export function newJobLoad() {
  return { pallet: null, truck: EMPTY_PICK, driver: EMPTY_PICK, destination: DEFAULT_DESTINATION, requestId: newRequestId() };
}

const LINK_RE = /(?:^|\/s\/)\s*(plt|bcn)[-\s]?(\d{1,9})\s*\/?\s*$/i;
const BCN_RE = /^BCN[-\s]?0*(\d{1,6})$/i;
const PLT_RE = /^PLT[-\s]?([0-9A-Z]{4})$/i;
const DIGITS_RE = /^0*(\d{1,6})$/;

const bcn = (n) => `BCN-${String(n).padStart(3, '0')}`;

/** What a scanned / typed entry names: {label, method: 'scan' | 'typed', code} or null (nothing typed).
 *  A beacon (BCN-031, bcn 31, a beacon's /s/bcn-31 link) -> BCN-031; a pallet label (PLT-9H2R) -> PLT-9H2R; a bare
 *  number -> "Pallet 7"; anything else is kept as typed. A link is always a scan; otherwise `scanned` (the keystroke
 *  timing) decides. The API checks the beacon (a free one is fine, one on a sales-order pallet is refused). */
export function palletEntry(text, scanned = false) {
  const raw = String(text == null ? '' : text).trim();
  if (!raw) return null;
  const link = /\/s\//i.test(raw);
  const method = scanned || link ? 'scan' : 'typed';
  let m = LINK_RE.exec(raw);
  if (m && (link || /^(plt|bcn)/i.test(raw))) {
    const n = parseInt(m[2], 10);
    if (m[1].toLowerCase() === 'bcn') return { label: bcn(n), method, code: bcn(n) };
    if (link) return { label: `Pallet #${n}`, method, code: `plt-${n}` };
  }
  const compact = raw.replace(/\s+/g, '').toUpperCase();
  m = BCN_RE.exec(compact);
  if (m) return { label: bcn(parseInt(m[1], 10)), method, code: bcn(parseInt(m[1], 10)) };
  m = PLT_RE.exec(compact);
  if (m) return { label: `PLT-${m[1]}`, method, code: `PLT-${m[1]}` };
  m = DIGITS_RE.exec(compact);
  if (m) return { label: `Pallet ${parseInt(m[1], 10)}`, method, code: String(parseInt(m[1], 10)) };
  const label = raw.slice(0, LABEL_MAX);
  return { label, method, code: label };
}

export const NEW_PALLET = Object.freeze({ label: 'New pallet', method: 'new', code: null });

/** The finalize_batch `load` for a load box and the task ids being shipped. */
export function jobLoadBody(box, taskIds) {
  const task_pallets = {};
  (taskIds || []).forEach((id) => { task_pallets[String(id)] = 1; });
  const t = truckFields(box.truck) || { vehicle_id: null, vehicle_text: null };
  const d = driverFields(box.driver) || { driver_user_id: null, driver_name: null };
  return {
    client_request_id: box.requestId,
    pallets: [{ seq: 1, label: box.pallet.label, method: box.pallet.method, code: box.pallet.code }],
    task_pallets,
    vehicle_id: t.vehicle_id,
    vehicle_text: t.vehicle_text,
    destination: String(box.destination || '').trim() || null,
    driver_user_id: d.driver_user_id,
    driver_name: d.driver_name,
  };
}

const styles = {
  input: { minHeight: 52, fontSize: '1.15rem' },
  btn: { minHeight: 48, fontSize: '1.02rem' },
};

/** Props: apiBase; value (newJobLoad() shape); onChange(next). */
export default function JobLoadBox({ apiBase, value, onChange }) {
  const { vehicles } = useVehicles(apiBase);
  const { drivers } = useDrivers(apiBase);
  const [entry, setEntry] = useState('');
  const timer = useRef(createEntryTimer());
  const set = (patch) => onChange({ ...value, ...patch });

  function commit(scanned) {
    const p = palletEntry(entry, scanned);
    timer.current.reset();
    if (!p) return;
    setEntry('');
    set({ pallet: p });
  }

  return (
    <div className="card mb-3 border-primary" data-testid="job-load-box">
      <div className="card-body">
        <h5 className="card-title">Load the ticked jobs</h5>
        <div className="small text-muted mb-3">
          Every load names a pallet: scan its beacon or label, type the number, or start a new pallet. Anything not loaded
          here still ships on the nightly Bad Idea auto-ship, just without a load record.
        </div>
        <div className="row g-3">
          <div className="col-lg-5">
            <label className="form-label fw-semibold" htmlFor="job-load-pallet">Pallet *</label>
            {value.pallet ? (
              <div className="d-flex flex-wrap align-items-center gap-2" data-testid="job-load-pallet-chosen">
                <span className="badge bg-primary" style={{ fontSize: '1.1rem', padding: '10px 14px' }}>{value.pallet.label}</span>
                <span className="text-muted">
                  {value.pallet.method === 'new' ? 'new pallet, no beacon' : value.pallet.method === 'scan' ? 'scanned' : 'typed'}
                </span>
                <button type="button" className="btn btn-outline-secondary" style={styles.btn} onClick={() => set({ pallet: null })}>
                  Change
                </button>
              </div>
            ) : (
              <>
                <div className="d-flex gap-2">
                  <input id="job-load-pallet" type="text" className="form-control" style={styles.input}
                    data-own-scans="true" autoComplete="off" placeholder="Scan or type BCN-031 / PLT-9H2R"
                    value={entry}
                    onChange={(e) => setEntry(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') { e.preventDefault(); commit(timer.current.wasScan()); return; }
                      timer.current.key(e);
                    }} />
                  <button type="button" className="btn btn-outline-primary" style={styles.btn} disabled={!entry.trim()}
                    onClick={() => commit(false)}>
                    Use it
                  </button>
                </div>
                <button type="button" className="btn btn-outline-secondary mt-2" style={styles.btn}
                  onClick={() => { setEntry(''); set({ pallet: NEW_PALLET }); }}>
                  New pallet without a beacon
                </button>
              </>
            )}
          </div>
          <div className="col-lg-3">
            <TruckPicker id="job-load-truck" label="Truck" vehicles={vehicles} value={value.truck}
              onChange={(truck) => set({ truck })} allowNone noneLabel="No truck: picked up at our dock" />
          </div>
          <div className="col-lg-4">
            <label className="form-label fw-semibold" htmlFor="job-load-destination">Going to</label>
            <input id="job-load-destination" type="text" className="form-control" style={styles.input} maxLength={120}
              value={value.destination} onChange={(e) => set({ destination: e.target.value })} />
          </div>
          <div className="col-lg-5">
            <DriverPicker id="job-load-driver" label="Driver (optional)" drivers={drivers} value={value.driver}
              onChange={(driver) => set({ driver })} />
          </div>
        </div>
      </div>
    </div>
  );
}
